import {
  AwardHistory,
  AwardRecordFacts,
  AwardView,
  DocumentList,
  DraftSaved,
  EntryLetterWording,
  EntryPreview,
  MyAwards,
  PeopleLookup,
  PeopleSheetMatch,
  PersonCard,
  SavedGroup,
  SavedGroups,
  UploadDocumentResponse,
  type AwardTypeCode,
  type CancelAward,
  type ConvertAward,
  type CreateDraft,
  type DocumentPurpose,
  type SaveDraft,
  type SaveGroup,
  type SubmitAward,
  type UpdateGroup,
} from '@myrewards/contracts';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request, requestDownload } from './client';

// Award entry and the award record (SL2–SL6, R2, D1, G1): every call goes through `request()`,
// so the session cookie, the CSRF header and contract validation apply to all of them.

const award = (id: string): string => `/api/v1/awards/${encodeURIComponent(id)}`;

/** A colleague by id, as the picker shows them: Recognize started from Search & jump (APP-022). */
export function personCardQuery(id: string) {
  return queryOptions({
    queryKey: ['people', 'card', id] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/people/${encodeURIComponent(id)}`, { schema: PersonCard, signal }),
    staleTime: 60_000,
    retry: false,
  });
}

/** The people picker: names, titles and sectors only, from two characters (APP-030). */
export function peopleLookupQuery(q: string) {
  const term = q.trim();
  return queryOptions({
    queryKey: ['people', 'lookup', term] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/people/lookup?q=${encodeURIComponent(term)}`, {
        schema: PeopleLookup,
        signal,
      }),
    enabled: term.length >= 2,
    staleTime: 60_000,
  });
}

/** A live check, with the form it checked: what it says is only about what that form held. */
export type CheckedPreview = EntryPreview & { checked: CreateDraft };

/**
 * The live calculation while entering: amounts, "Paid from", the budget check and who gets the
 * letters. Nothing is saved.
 * The key is the whole body, so a newer body replaces the request in flight (its signal aborts it).
 * The answer carries the body it was for, so the form can tell which of its issues are about what
 * is on screen now and which a newer check is still working out (APP-066).
 */
export function previewQuery(body: CreateDraft | null) {
  return queryOptions({
    queryKey: ['awards', 'preview', body] as const,
    queryFn: async ({ signal }): Promise<CheckedPreview> => {
      const result = await request('/api/v1/awards/preview', {
        method: 'POST',
        body,
        schema: EntryPreview,
        signal,
      });
      // Enabled only with a body.
      return { ...result, checked: body as CreateDraft };
    },
    enabled: body !== null,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: false,
    meta: { busy: true },
  });
}

const WORDING_PATH = '/api/v1/awards/preview/letter-wording?';

/**
 * APP-201: where a Ready design's letter puts the award's wording, from the path the preview gave
 * (`EntryLetterDesign.wording`). A version's sentence never changes, so it's read once a session.
 */
export function letterWordingQuery(path: string | null) {
  return queryOptions({
    queryKey: ['awards', 'letter-wording', path] as const,
    queryFn: ({ signal }) => {
      // Only the server's own path, never anything else a preview might hold.
      if (path === null || !path.startsWith(WORDING_PATH)) return { context: null };
      return request(path, { schema: EntryLetterWording, signal });
    },
    enabled: path !== null,
    staleTime: Infinity,
    retry: false,
  });
}

/** The award view as the page reads it (the contract now carries `fundingMode`). */
export const AwardDetail = AwardView;
export type AwardDetail = z.infer<typeof AwardDetail>;

/** One award as this person may see it: the draft form or the record (G1). */
export function awardQuery(id: string) {
  return queryOptions({
    queryKey: ['awards', 'one', id] as const,
    queryFn: ({ signal }) => request(award(id), { schema: AwardDetail, signal }),
    staleTime: 10_000,
  });
}

/**
 * "My drafts & submitted" for one year, a page of what was sent at a time (QA-014). With no year,
 * the current one; with no page, the first.
 */
export function myAwardsPageQuery(
  view: { year?: number | null; page?: number | null; size?: number | null } = {},
) {
  const params = new URLSearchParams();
  if (view.year != null) params.set('year', String(view.year));
  if (view.page != null && view.page > 1) params.set('page', String(view.page));
  if (view.size != null) params.set('size', String(view.size));
  const query = params.toString();
  return queryOptions({
    queryKey: ['awards', 'mine', view.year ?? null, view.page ?? 1, view.size ?? null] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/awards/mine${query === '' ? '' : `?${query}`}`, {
        schema: MyAwards,
        signal,
      }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** This year's drafts and the first page of what was sent (Recognize, and the page's default). */
export const myAwardsQuery = myAwardsPageQuery();

/** This person's saved groups (APP-057). */
export const savedGroupsQuery = queryOptions({
  queryKey: ['saved-groups'] as const,
  queryFn: ({ signal }) => request('/api/v1/saved-groups', { schema: SavedGroups, signal }),
  staleTime: 60_000,
});

export function createGroup(body: SaveGroup): Promise<SavedGroup> {
  return request('/api/v1/saved-groups', { method: 'POST', body, schema: SavedGroup });
}

export function updateGroup(id: string, body: UpdateGroup): Promise<SavedGroup> {
  return request(`/api/v1/saved-groups/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
    schema: SavedGroup,
  });
}

export function deleteGroup(id: string, version: number): Promise<void> {
  return request(`/api/v1/saved-groups/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: { version },
    schema: z.undefined(),
  });
}

/**
 * Reads a sheet of people for this kind of award and says whom each row matches (APP-057). With
 * `forSectorLead`, it's read for the sector's Sector Lead a Stock Admin is entering it for (R15).
 */
export function matchPeopleSheet(
  file: File,
  type: AwardTypeCode,
  sectorId: string | null,
  forSectorLead = false,
): Promise<PeopleSheetMatch> {
  const fields: Record<string, string> = { type };
  if (sectorId !== null) fields['sectorId'] = sectorId;
  if (forSectorLead) fields['forSectorLead'] = 'true';
  return request('/api/v1/awards/people-sheet', {
    method: 'POST',
    body: filePart(file, fields),
    schema: PeopleSheetMatch,
  });
}

/** The award's trail (G1), in plain words. */
/**
 * The record beyond the award itself (G1, APP-069): its stages in words, who entered and approved
 * it, each person's facts and value over time, and what this reader may do with its files.
 */
export function awardRecordQuery(id: string) {
  return queryOptions({
    queryKey: ['awards', 'record', id] as const,
    queryFn: ({ signal }) => request(`${award(id)}/record`, { schema: AwardRecordFacts, signal }),
    staleTime: 10_000,
  });
}

export function awardHistoryQuery(id: string) {
  return queryOptions({
    queryKey: ['awards', 'history', id] as const,
    queryFn: ({ signal }) => request(`${award(id)}/history`, { schema: AwardHistory, signal }),
    staleTime: 10_000,
  });
}

/**
 * Export record (G1, APP-065): the whole record as a PDF, made on the server from what this person
 * may see and recorded on the award's trail. Refused in Preview.
 */
export function exportAwardRecord(id: string, signal?: AbortSignal): Promise<Blob> {
  return requestDownload(`${award(id)}/export`, {
    contentType: 'application/pdf',
    maxBytes: 25 * 1024 * 1024,
    ...(signal !== undefined && { signal }),
  });
}

/** "SDB-2026-0158-record.pdf": the award's number only, never a person's name (as the server). */
export function recordFileName(displayId: string | null): string {
  const safe = displayId?.replace(/[^A-Za-z0-9-]/g, '') ?? '';
  return safe === '' ? 'award-record.pdf' : `${safe}-record.pdf`;
}

/** The award's current files, each with where its virus check is. */
export function documentsQuery(id: string) {
  return queryOptions({
    queryKey: ['awards', 'documents', id] as const,
    queryFn: ({ signal }) => request(`${award(id)}/documents`, { schema: DocumentList, signal }),
    staleTime: 5_000,
  });
}

/** Where a file downloads from: a plain same-origin link, so the browser saves it. */
export function documentHref(awardId: string, documentId: string): string {
  return `${award(awardId)}/documents/${encodeURIComponent(documentId)}/content`;
}

export function createDraft(body: CreateDraft): Promise<DraftSaved> {
  return request('/api/v1/awards', { method: 'POST', body, schema: DraftSaved });
}

export function saveDraft(id: string, body: SaveDraft): Promise<DraftSaved> {
  return request(award(id), { method: 'PUT', body, schema: DraftSaved });
}

export function submitAward(id: string, body: SubmitAward): Promise<AwardView> {
  return request(`${award(id)}/submit`, { method: 'POST', body, schema: AwardView });
}

export function cancelAward(id: string, body: CancelAward): Promise<AwardView> {
  return request(`${award(id)}/cancel`, { method: 'POST', body, schema: AwardView });
}

/** PAA ↔ TAA: the other type's draft from the same people; the original ends cancelled. */
export function convertAward(id: string, body: ConvertAward): Promise<DraftSaved> {
  return request(`${award(id)}/convert`, { method: 'POST', body, schema: DraftSaved });
}

function filePart(file: File, fields: Record<string, string> = {}): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  // The file goes last, so the server reads the fields first.
  form.append('file', file, file.name);
  return form;
}

export function uploadDocument(
  awardId: string,
  file: File,
  purpose: DocumentPurpose,
): Promise<UploadDocumentResponse> {
  return request(`${award(awardId)}/documents`, {
    method: 'POST',
    body: filePart(file, { purpose }),
    schema: UploadDocumentResponse,
  });
}

export function replaceDocument(
  awardId: string,
  documentId: string,
  file: File,
): Promise<UploadDocumentResponse> {
  return request(`${award(awardId)}/documents/${encodeURIComponent(documentId)}/replace`, {
    method: 'POST',
    body: filePart(file),
    schema: UploadDocumentResponse,
  });
}

/** Deleting keeps the file in the history (nothing used is hard-deleted); it needs a reason. */
export function deleteDocument(awardId: string, documentId: string, reason: string): Promise<void> {
  return request(`${award(awardId)}/documents/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    body: { reason },
    schema: z.undefined(),
  });
}
